import React from 'react';
import { IconInfoCircle } from '@tabler/icons-react';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import './StandardCriteriaDisplay.css';

/**
 * StandardCriteriaDisplay - Displays Step Therapy and Prior Authorization criteria
 * @param {Object} props
 * @param {string} props.type - "formulary" or "medical"
 * @param {Object} props.stDetail - Step Therapy details
 * @param {Object} props.paDetail - Prior Authorization details
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {string} props.title - Card title
 */
const StandardCriteriaDisplay = ({
  type,
  stDetail,
  paDetail,
  loading,
  error,
  title
}) => {
  const hasStepTherapy = stDetail && stDetail.StepStatus !== 'No Step' && stDetail.StepStatus !== 'Not Listed';
  const hasPriorAuth = paDetail?.PAOverall === 'Restrictive' || paDetail?.PAOverall === 'Not Covered';

  if (loading) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 space-y-4">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        <div className="space-y-2">
          <div className="h-4 bg-muted/30 rounded animate-pulse"></div>
          <div className="h-4 bg-muted/30 rounded animate-pulse"></div>
          <div className="h-4 bg-muted/30 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-50 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <i className="bi bi-info-circle"></i>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  if (!stDetail && !paDetail) {
    return (
      <div className="bg-slate-50 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <i className="bi bi-check-circle-fill text-success text-2xl mb-2"></i>
          <p className="font-semibold text-sm mb-1">No restrictions</p>
          <p className="text-muted-foreground text-xs mb-0">No standard criteria requirements for this indication</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-6">
      {/* Title */}
      <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>

      {/* Step Therapy Section */}
      <div className="space-y-3">
        <h6 className="text-xs font-semibold text-foreground uppercase tracking-wide">Step Therapy</h6>

        {stDetail ? (
          <div className="bg-white rounded-md border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr className="bg-muted/30">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Statement</th>
                    {type === 'formulary' && (
                      <>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Generic Req.</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Brand Req.</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {stDetail.StepStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{stDetail.StepStatement || 'N/A'}</td>
                    {type === 'formulary' && (
                      <>
                        <td className="px-4 py-3 text-sm text-foreground">{stDetail.MinRequiredGenericCount}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{stDetail.MinRequiredBrandCount}</td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <i className="bi bi-check-circle-fill text-success"></i>
            <p className="mb-0">No step therapy required</p>
          </div>
        )}
      </div>

      {/* Prior Authorization Section */}
      <div className="space-y-3 pt-4 border-t border-border">
        <h6 className="text-xs font-semibold text-foreground uppercase tracking-wide">Prior Authorization</h6>

        {paDetail ? (
          <div className="bg-white rounded-md border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr className="bg-muted/30">
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Overall</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Age</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Gender</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Duration</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Specialist</th>
                    {type === 'formulary' && (
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">
                        <span className="flex items-center gap-1">
                          Medicare Part B vs. Part D
                          <Popover>
                            <PopoverTrigger asChild>
                              <IconInfoCircle size={12} className="text-muted-foreground cursor-help" />
                            </PopoverTrigger>
                            <PopoverContent className="w-64 text-xs normal-case font-normal leading-relaxed">
                              <strong>Yes:</strong> Policy states a Medicare Part B vs. Part D determination applies — coverage under Part D will be denied if coverage is available under Part B.
                              <br /><br />
                              <strong>No:</strong> Policy does not state a Medicare Part B vs. Part D determination.
                            </PopoverContent>
                          </Popover>
                        </span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        hasPriorAuth ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {paDetail.PAOverall}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{paDetail.Age || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{paDetail.Gender || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{paDetail.Duration || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{paDetail.Specialist || 'N/A'}</td>
                    {type === 'formulary' && <td className="px-4 py-3 text-sm text-foreground">{paDetail.BvsD || 'N/A'}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <i className="bi bi-check-circle-fill text-success"></i>
            <p className="mb-0">No prior authorization required</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardCriteriaDisplay;
